import { useMemo } from "react";

import RenderIf from "common/components/RenderIf";
import ProfileImage from "common/components/ui/ProfileImage";
import { Tag } from "common/components/ui/Tag";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

export default function Accounts() {
  const isDesktop = useTwBreakpoint("lg");
  const { accounts, rental_subscription } = useSelectedWorkspaceContext();

  const rentedAccounts = useMemo(
    () => accounts.filter(({ type }) => type === "rental"),
    [accounts],
  );

  const expectedRentedCount = rental_subscription.rented_count;
  const maxAccounts = expectedRentedCount === 11 ? 11 : 10;
  const pendingAccountsCount = expectedRentedCount - rentedAccounts.length;

  const combinedAccounts = [
    ...rentedAccounts,
    ...Array.from({ length: pendingAccountsCount }, (_, i) => ({
      type: "pending" as const,
      id: `pending-account-${i + 1}`,
    })),
  ];

  return (
    <div className="flex flex-col gap-y-1 items-start">
      <div className="flex items-center gap-x-1">
        <h5 className="text-sm-medium text-black-500">Accounts</h5>
        <Tag variant="tertiary" size="sm">
          {expectedRentedCount}
        </Tag>
      </div>

      <Tooltip>
        <TooltipTrigger className="flex -space-x-2.5">
          {combinedAccounts
            .slice(0, maxAccounts)
            .map((item) =>
              item.type === "pending" ? (
                <span
                  key={item.id}
                  className="size-6 bg-black-50 rounded-full border-black-400 border border-dashed"
                />
              ) : (
                <ProfileImage
                  key={item.id}
                  src={item.picture_url}
                  alt={item.full_name}
                  size="xs"
                  className="ring-1 ring-white"
                />
              ),
            )}

          <RenderIf condition={expectedRentedCount > maxAccounts}>
            <span className="inline-flex ring-1 ring-white bg-purple-100 size-6 rounded-full items-center justify-center text-purple-600 text-xxs-bold">
              +{expectedRentedCount - maxAccounts}
            </span>
          </RenderIf>
        </TooltipTrigger>

        <TooltipContent side={isDesktop ? "right" : "top"}>
          <div className="flex flex-col gap-y-1.5 max-h-60 overflow-y-auto scrollbar-thin -my-4 py-4">
            {rentedAccounts.map(({ id, picture_url, full_name }) => (
              <div key={id} className="flex items-center gap-x-1">
                <ProfileImage size="2xs" src={picture_url} />
                <span className="text-xs">{full_name}</span>
              </div>
            ))}

            <RenderIf condition={pendingAccountsCount > 0}>
              <span className="text-xs">
                {rentedAccounts.length > 0 && "+ "}
                {pendingAccountsCount} Account
                {pendingAccountsCount > 1 ? "s" : ""} pending
              </span>
            </RenderIf>
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
